import {
  AfterViewInit,
  ChangeDetectorRef,
  NgZone,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Subject,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  merge,
  skip,
  takeUntil,
} from 'rxjs';
import { AiChatOverlayService } from '@services/helpers/ai-chat-overlay.service';
import { FeedbackTriggerService } from '@services/helpers/feedback-trigger.service';
import { SideMenuService } from '@shared/services/side-menu.service';
import { FeedbackPanelComponent } from './feedback-panel/feedback-panel.component';

/**
 * Classe do container que rola o conteúdo do dashboard (`dashboard.component.html`). Renomeou lá, renomeie
 * aqui: sem ela o botão para de recolher com a página rolada, sem erro nenhum (card 3822).
 */
const DASHBOARD_SCROLL_CONTAINER_CLASS = 'dashboard__main-content';

@Component({
  selector: 'app-floating-feedback-button',
  standalone: true,
  imports: [CommonModule, FeedbackPanelComponent],
  templateUrl: './floating-feedback-button.component.html',
  styleUrls: ['./floating-feedback-button.component.scss'],
})
export class FloatingFeedbackButtonComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('wrapper') wrapperRef?: ElementRef<HTMLElement>;
  @ViewChild('fab') fabRef?: ElementRef<HTMLButtonElement>;

  isPanelOpen = false;
  isDragging = false;
  screenshotBlob: Blob | null = null;
  isCapturingScreenshot = false;
  isRecording = false;
  recordingElapsedSeconds = 0;
  showBubble = false;
  isHidden = false;
  contextMenuVisible = false;
  fabFaded = false;
  /**
   * Página rolada: o FAB recolhe para a margem direita (fica uma borda à vista) e volta no hover/foco,
   * para não cobrir botões e números do conteúdo que passa por baixo (card 3822, U10).
   */
  fabTucked = false;
  /** true = balão à esquerda do FAB (metade direita da viewport); false = à direita do FAB (metade esquerda). */
  bubbleOpensLeft = true;

  protected position: { left: number; top: number } | null = null;

  private readonly storageKey = 'floating-feedback-button-position-v2';
  /** Chave legada: removida no init para quem tinha dismiss permanente. */
  private readonly legacyBubbleDismissedKey = 'floating-feedback-bubble-dismissed';
  private readonly hideUntilKey = 'floating-feedback-button-hidden-until';
  private readonly fabHideDelayMs = 24 * 60 * 60 * 1000;
  private readonly bubbleReshowDelayMs = 10 * 60 * 1000;
  private readonly buttonSize = 52;
  private readonly padding = 8;
  private dragOffset = { x: 0, y: 0 };
  private hasDragged = false;
  private suppressClick = false;
  private captureAborted = false;
  private bubbleReshowTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private fabHideTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private contextMenuDismissPointerDown?: (event: PointerEvent) => void;
  private contextMenuDismissScroll?: () => void;
  private contextMenuDismissWheel?: () => void;
  private pageLoadFadeTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private hoverLeaveFadeTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private pageLoadFadeConsumed = false;
  private readonly pageLoadFadeDelayMs = 5000;
  private readonly hoverLeaveFadeDelayMs = 400;
  private bubbleWasOpenBeforeFade = false;
  /** Modal ou drawer aberto: o FAB vai para cima do Cog AI e o balão some (o template lê). */
  protected isInModalMode = false;
  private recordingTimerInterval: ReturnType<typeof setInterval> | null = null;
  private positionBeforeModal: { left: number; top: number } | null = null;
  private modalObserver: MutationObserver | null = null;
  private modalSyncTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private readonly modalSyncDelayMs = 100;
  /** Rolagem (px) a partir da qual o FAB recolhe para a margem. */
  private readonly tuckScrollThresholdPx = 80;
  private readonly tuckOnScroll = (event: Event): void => this.updateTuckedState(event);
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly ngZone: NgZone,
    private readonly aiChatOverlayService: AiChatOverlayService,
    private readonly sideMenuService: SideMenuService,
    private readonly feedbackTriggerService: FeedbackTriggerService,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {}

  ngOnInit(): void {
    this.checkHiddenState();
    this.watchAiButtonVisibility();
    this.watchFeedbackTrigger();
    if (isPlatformBrowser(this.platformId)) {
      this.clearStoredFabPosition();
      this.watchMenuLayoutReset();
      this.watchSideMenuGeometry();
      this.watchMenuLayoutForClassRefresh();
    }
    try {
      localStorage.removeItem(this.legacyBubbleDismissedKey);
    } catch {
      // Ignora quota / modo privado
    }
    if (!this.isHidden) this.showBubble = true;
  }

  /** Menu superior (barra fixa): classe CSS desce o FAB no eixo Y; `right` mantém-se. */
  isTopMenuLayout(): boolean {
    return this.sideMenuService.menuLayout === 'top';
  }

  ngAfterViewInit(): void {
    this.scheduleBubbleAlignRefresh();
    this.registerContextMenuDismissListeners();
    this.setupModalObserver();
    this.schedulePageLoadFade();
    if (isPlatformBrowser(this.platformId)) {
      // Captura: o conteúdo do dashboard rola num container próprio, não no document.
      // Fora da zona: rolar não dispara um ciclo de detecção do app inteiro; `updateTuckedState` chama
      // `detectChanges` só quando o estado muda.
      this.ngZone.runOutsideAngular(() =>
        document.addEventListener('scroll', this.tuckOnScroll, { capture: true, passive: true })
      );
    }
  }

  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId)) {
      document.removeEventListener('scroll', this.tuckOnScroll, { capture: true });
    }
    this.unregisterContextMenuDismissListeners();
    this.teardownModalObserver();
    this.clearModalSyncTimer();
    this.clearBubbleReshowTimer();
    this.clearFabHideTimer();
    this.clearPageLoadFadeTimer();
    this.clearHoverLeaveFadeTimer();
    this.clearRecordingTimer();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private clearRecordingTimer(): void {
    if (this.recordingTimerInterval !== null) {
      clearInterval(this.recordingTimerInterval);
      this.recordingTimerInterval = null;
    }
  }

  closeBubble(): void {
    this.contextMenuVisible = false;
    this.clearBubbleReshowTimer();
    this.showBubble = false;
    this.bubbleReshowTimeoutId = setTimeout(() => {
      this.bubbleReshowTimeoutId = null;
      this.contextMenuVisible = false;
      this.showBubble = true;
      this.cdr.detectChanges();
      this.scheduleBubbleAlignRefresh();
    }, this.bubbleReshowDelayMs);
    this.cdr.detectChanges();
  }

  onContextMenu(event: MouseEvent): void {
    event.preventDefault();
    this.refreshBubbleHorizontalAlign();
    this.showBubble = false;
    this.contextMenuVisible = true;
    this.cdr.detectChanges();
  }

  private closeContextMenu(): void {
    if (!this.contextMenuVisible) return;
    this.contextMenuVisible = false;
    this.restoreBubbleAfterContextMenu();
    this.cdr.detectChanges();
  }

  /**
   * Fecha o menu antes dos handlers do PrimeNG/menu lateral (bubble `click` não chega ao document).
   * `pointerdown` em capture no window + scroll/wheel para barra de rolagem.
   */
  private registerContextMenuDismissListeners(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this.contextMenuDismissPointerDown = (event: PointerEvent) => {
      if (!this.contextMenuVisible || event.button === 2) return;

      const target = event.target as HTMLElement;
      if (target.closest('.feedback-context-menu__item')) {
        this.hideButton();
        return;
      }

      this.closeContextMenu();
    };

    this.contextMenuDismissScroll = () => {
      if (this.contextMenuVisible) this.closeContextMenu();
    };

    this.contextMenuDismissWheel = () => {
      if (this.contextMenuVisible) this.closeContextMenu();
    };

    window.addEventListener('pointerdown', this.contextMenuDismissPointerDown, true);
    document.addEventListener('scroll', this.contextMenuDismissScroll, { passive: true });
    window.addEventListener('wheel', this.contextMenuDismissWheel, {
      capture: true,
      passive: true,
    });
  }

  private unregisterContextMenuDismissListeners(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    if (this.contextMenuDismissPointerDown) {
      window.removeEventListener('pointerdown', this.contextMenuDismissPointerDown, true);
      this.contextMenuDismissPointerDown = undefined;
    }
    if (this.contextMenuDismissScroll) {
      document.removeEventListener('scroll', this.contextMenuDismissScroll);
      this.contextMenuDismissScroll = undefined;
    }
    if (this.contextMenuDismissWheel) {
      window.removeEventListener('wheel', this.contextMenuDismissWheel, true);
      this.contextMenuDismissWheel = undefined;
    }
  }

  hideButton(): void {
    this.contextMenuVisible = false;
    this.isHidden = true;
    const until = Date.now() + this.fabHideDelayMs;
    this.clearFabHideTimer();
    try {
      localStorage.setItem(this.hideUntilKey, String(until));
    } catch {
      // Ignora quota / modo privado
    }
    this.fabHideTimeoutId = setTimeout(() => this.revealFabAfterHide(), this.fabHideDelayMs);
    this.cdr.detectChanges();
  }

  onClick(): void {
    if (this.suppressClick || this.isPanelOpen) return;
    this.contextMenuVisible = false;
    this.captureAborted = false;
    this.isPanelOpen = true;
    this.cdr.detectChanges();
  }

  onMinimizeClick(event: MouseEvent): void {
    event.stopPropagation();
    this.hideButton();
  }

  onFabMouseEnter(): void {
    this.clearHoverLeaveFadeTimer();
    this.clearPageLoadFadeTimer();
    this.revealFab();
  }

  onFabMouseLeave(): void {
    this.clearHoverLeaveFadeTimer();
    this.hoverLeaveFadeTimeoutId = setTimeout(() => {
      this.hoverLeaveFadeTimeoutId = null;
      this.applyFade();
    }, this.hoverLeaveFadeDelayMs);
  }

  onInitialCaptureScreenshot(): void {
    this.captureAborted = false;
    this.isCapturingScreenshot = true;
    this.cdr.detectChanges();
    void this.captureScreenshot({ skipPanelDelay: true });
  }

  onRecaptureScreenshot(): void {
    this.captureAborted = false;
    this.isCapturingScreenshot = true;
    this.cdr.detectChanges();
    void this.captureScreenshot({ skipPanelDelay: true });
  }

  onClosePanel(): void {
    this.captureAborted = true;
    this.isPanelOpen = false;
    this.isCapturingScreenshot = false;
    this.scheduleBubbleAlignRefresh();
  }

  onFeedbackSubmitted(): void {
    this.screenshotBlob = null;
  }

  onClearScreenshot(): void {
    this.screenshotBlob = null;
  }

  onRecordingStateChanged(recording: boolean): void {
    this.isRecording = recording;
    if (recording) {
      this.recordingElapsedSeconds = 0;
      this.recordingTimerInterval = setInterval(() => {
        this.recordingElapsedSeconds++;
        this.cdr.detectChanges();
      }, 1000);
    } else {
      this.clearRecordingTimer();
    }
    this.cdr.detectChanges();
  }

  get recordingTimeDisplay(): string {
    const m = Math.floor(this.recordingElapsedSeconds / 60);
    const s = this.recordingElapsedSeconds % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }

  startDrag(event: MouseEvent | TouchEvent): void {
    const point = this.getPoint(event);
    if (!point || !this.wrapperRef) return;

    event.preventDefault();
    if (this.contextMenuVisible) {
      this.closeContextMenu();
    }
    this.isDragging = true;
    this.hasDragged = false;

    const rect = this.wrapperRef.nativeElement.getBoundingClientRect();
    this.dragOffset = { x: point.x - rect.left, y: point.y - rect.top };

    if (!this.position) {
      this.position = { left: rect.left, top: rect.top };
    }
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    this.handleMove(event);
  }

  @HostListener('document:touchmove', ['$event'])
  onTouchMove(event: TouchEvent): void {
    this.handleMove(event);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (this.isInModalMode) {
      this.position = this.calculateAboveChatAiPosition();
      this.scheduleBubbleAlignRefresh();
      this.cdr.detectChanges();
      return;
    }
    if (this.position) {
      this.clampFabPositionToSafeArea();
      this.cdr.detectChanges();
    }
    this.scheduleBubbleAlignRefresh();
  }

  @HostListener('document:mouseup')
  @HostListener('document:touchend')
  onEndDrag(): void {
    if (!this.isDragging) return;
    this.isDragging = false;

    if (this.hasDragged) {
      this.suppressClick = true;
      setTimeout(() => (this.suppressClick = false), 50);
    }
    this.scheduleBubbleAlignRefresh();
  }

  private checkHiddenState(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.clearFabHideTimer();
    try {
      const raw = localStorage.getItem(this.hideUntilKey);
      if (!raw) {
        this.isHidden = false;
        return;
      }
      const until = parseInt(raw, 10);
      if (isNaN(until) || Date.now() >= until) {
        localStorage.removeItem(this.hideUntilKey);
        this.isHidden = false;
        return;
      }
      this.isHidden = true;
      const remainingMs = until - Date.now();
      this.fabHideTimeoutId = setTimeout(() => this.revealFabAfterHide(), remainingMs);
    } catch {
      this.isHidden = false;
    }
  }

  private revealFabAfterHide(): void {
    this.fabHideTimeoutId = null;
    this.isHidden = false;
    try {
      localStorage.removeItem(this.hideUntilKey);
    } catch {
      // Ignora quota / modo privado
    }
    if (this.bubbleReshowTimeoutId === null) {
      this.showBubble = true;
      this.scheduleBubbleAlignRefresh();
    }
    this.pageLoadFadeConsumed = false;
    this.schedulePageLoadFade();
    this.cdr.detectChanges();
  }

  private clearFabHideTimer(): void {
    if (this.fabHideTimeoutId !== null) {
      clearTimeout(this.fabHideTimeoutId);
      this.fabHideTimeoutId = null;
    }
  }

  private schedulePageLoadFade(): void {
    if (this.pageLoadFadeConsumed) {
      return;
    }
    this.clearPageLoadFadeTimer();
    this.pageLoadFadeTimeoutId = setTimeout(() => {
      this.pageLoadFadeTimeoutId = null;
      this.pageLoadFadeConsumed = true;
      this.applyFade();
    }, this.pageLoadFadeDelayMs);
  }

  /**
   * Recolhe o FAB quando a área rolada passou do limite. Não mexe quando a pessoa arrastou o botão (ela
   * escolheu o lugar), durante gravação ou com o painel aberto.
   */
  private updateTuckedState(event: Event): void {
    const target = event.target;
    // Só a rolagem da página conta: a do documento ou a da área principal do dashboard, que rola num
    // container próprio. Lista de dropdown, modal ou tabela larga rolando não mexe no botão.
    const isPageScroll =
      !(target instanceof Element) ||
      target === document.scrollingElement ||
      target.classList.contains(DASHBOARD_SCROLL_CONTAINER_CLASS);
    if (!isPageScroll) return;
    const scrollTop =
      target instanceof Element ? target.scrollTop : (document.scrollingElement?.scrollTop ?? 0);
    const next = scrollTop > this.tuckScrollThresholdPx;
    if (next === this.fabTucked) return;
    this.fabTucked = next;
    this.cdr.detectChanges();
  }

  private applyFade(): void {
    this.fabFaded = true;
    if (this.showBubble && !this.isInModalMode) {
      this.bubbleWasOpenBeforeFade = true;
      this.showBubble = false;
    }
    this.cdr.detectChanges();
  }

  private revealFab(): void {
    if (!this.fabFaded && !this.bubbleWasOpenBeforeFade) {
      return;
    }
    this.fabFaded = false;
    if (this.bubbleWasOpenBeforeFade) {
      this.bubbleWasOpenBeforeFade = false;
      this.showBubble = true;
      this.scheduleBubbleAlignRefresh();
    }
    this.cdr.detectChanges();
  }

  private clearPageLoadFadeTimer(): void {
    if (this.pageLoadFadeTimeoutId !== null) {
      clearTimeout(this.pageLoadFadeTimeoutId);
      this.pageLoadFadeTimeoutId = null;
    }
  }

  private clearHoverLeaveFadeTimer(): void {
    if (this.hoverLeaveFadeTimeoutId !== null) {
      clearTimeout(this.hoverLeaveFadeTimeoutId);
      this.hoverLeaveFadeTimeoutId = null;
    }
  }

  private setupModalObserver(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.modalObserver = new MutationObserver(() => this.scheduleModalStateSync());
    this.modalObserver.observe(document.body, { childList: true, subtree: true });
  }

  private teardownModalObserver(): void {
    this.clearModalSyncTimer();
    this.modalObserver?.disconnect();
    this.modalObserver = null;
  }

  /** Evita enter/exit em frames intermediários da animação PrimeNG (falso negativo na máscara). */
  private scheduleModalStateSync(): void {
    this.clearModalSyncTimer();
    this.modalSyncTimeoutId = setTimeout(() => {
      this.modalSyncTimeoutId = null;
      this.syncModalModeState();
    }, this.modalSyncDelayMs);
  }

  private clearModalSyncTimer(): void {
    if (this.modalSyncTimeoutId !== null) {
      clearTimeout(this.modalSyncTimeoutId);
      this.modalSyncTimeoutId = null;
    }
  }

  private syncModalModeState(): void {
    const hasModal = this.isModalOpenInDom();
    if (hasModal && !this.isInModalMode) {
      this.enterModalMode();
    } else if (!hasModal && this.isInModalMode) {
      this.exitModalMode();
    }
  }

  private isModalOpenInDom(): boolean {
    // Drawer sem máscara (ex.: "Detalhes do documento" do Data Room) também conta: no canto de cima o FAB cobria o X
    // de fechar dele (crítica de design do PBI 3800).
    const masks = Array.from(
      document.querySelectorAll<HTMLElement>('.p-dialog-mask, .p-overlay-mask, .p-drawer')
    );
    for (const el of masks) {
      if (this.isDomElementVisible(el)) return true;
    }
    const confirmDialog = document.querySelector<HTMLElement>('p-confirmdialog .p-dialog');
    return !!confirmDialog && this.isDomElementVisible(confirmDialog);
  }

  private isDomElementVisible(el: HTMLElement): boolean {
    if (!el.isConnected) return false;
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    const opacity = parseFloat(style.opacity);
    if (!Number.isNaN(opacity) && opacity <= 0) return false;
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  private enterModalMode(): void {
    this.isInModalMode = true;
    this.positionBeforeModal = this.position;
    this.cdr.detectChanges();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.isInModalMode || !this.isModalOpenInDom()) return;
        this.position = this.calculateAboveChatAiPosition();
        this.scheduleBubbleAlignRefresh();
        this.cdr.detectChanges();
      });
    });
  }

  private exitModalMode(): void {
    this.isInModalMode = false;
    this.position = this.positionBeforeModal;
    this.positionBeforeModal = null;
    this.scheduleBubbleAlignRefresh();
    this.cdr.detectChanges();
  }

  /**
   * Posiciona o FAB de feedback centralizado horizontalmente acima do FAB de Chat AI.
   * Seleciona o wrapper mais próximo do rodapé para ignorar instâncias inline de telas específicas.
   * Fallback para a posição padrão CSS do Chat AI (right: 24px, bottom: 24px) se nenhum for encontrado.
   */
  private calculateAboveChatAiPosition(): { left: number; top: number } {
    const gap = 12;
    const feedbackSize = this.buttonSize;

    const allFabs = Array.from(
      document.querySelectorAll<HTMLElement>('.floating-ai-button-wrapper')
    );
    if (allFabs.length > 0) {
      const globalFab = allFabs.reduce((best, el) => {
        const rect = el.getBoundingClientRect();
        const bestRect = best.getBoundingClientRect();
        const distFromBottom = Math.abs(window.innerHeight - rect.bottom);
        const bestDistFromBottom = Math.abs(window.innerHeight - bestRect.bottom);
        return distFromBottom < bestDistFromBottom ? el : best;
      });
      const rect = globalFab.getBoundingClientRect();
      return {
        left: rect.left + (rect.width - feedbackSize) / 2,
        top: rect.top - gap - feedbackSize,
      };
    }

    // Fallback: posição padrão do Chat AI (right: 24px, bottom: 24px, 78px)
    const chatAiRight = 24;
    const chatAiBottom = 24;
    const chatAiSize = this.getChatAiResponsiveSize();
    return {
      left: window.innerWidth - chatAiRight - chatAiSize + (chatAiSize - feedbackSize) / 2,
      top: window.innerHeight - chatAiBottom - chatAiSize - gap - feedbackSize,
    };
  }

  private getChatAiResponsiveSize(): number {
    const w = window.innerWidth;
    if (w <= 480) return 60;
    if (w <= 768) return 64;
    if (w <= 1200) return 68;
    return 78;
  }

  private handleMove(event: MouseEvent | TouchEvent): void {
    if (!this.isDragging || !this.wrapperRef) return;

    const point = this.getPoint(event);
    if (!point) return;

    event.preventDefault();

    const newLeft = point.x - this.dragOffset.x;
    const newTop = point.y - this.dragOffset.y;

    const minLeft = this.getMinLeftPxForFab();
    const clampedLeft = Math.min(
      Math.max(minLeft, newLeft),
      window.innerWidth - this.buttonSize - this.padding
    );
    const clampedTop = Math.min(
      Math.max(this.getMinTopPxForFab(), newTop),
      window.innerHeight - this.buttonSize - this.padding
    );

    this.hasDragged =
      this.hasDragged ||
      Math.abs((this.position?.left ?? 0) - clampedLeft) > 2 ||
      Math.abs((this.position?.top ?? 0) - clampedTop) > 2;

    this.position = { left: clampedLeft, top: clampedTop };
    this.refreshBubbleHorizontalAlign();
  }

  /**
   * Mantém o FAB dentro da viewport, à direita do menu lateral (`side`) e abaixo da barra superior (`top`).
   * @returns false se a área útil for inválida e a posição tiver sido limpa
   */
  private clampFabPositionToSafeArea(): boolean {
    if (!isPlatformBrowser(this.platformId) || !this.position) return true;

    const minLeft = this.getMinLeftPxForFab();
    const maxLeft = window.innerWidth - this.buttonSize - this.padding;
    const minTop = this.getMinTopPxForFab();
    const maxTop = window.innerHeight - this.buttonSize - this.padding;

    if (maxLeft < minLeft || maxTop < minTop) {
      this.position = null;
      this.clearStoredFabPosition();
      return false;
    }

    this.position = {
      left: Math.min(maxLeft, Math.max(minLeft, this.position.left)),
      top: Math.min(maxTop, Math.max(minTop, this.position.top)),
    };
    return true;
  }

  private getMinLeftPxForFab(): number {
    if (!isPlatformBrowser(this.platformId)) return this.padding;
    if (this.sideMenuService.menuLayout !== 'side') return this.padding;

    const aside = document.querySelector('aside.side-menu');
    if (!aside) return this.padding;

    const rect = aside.getBoundingClientRect();
    if (rect.width <= 0 || rect.right <= 0) return this.padding;

    return Math.max(this.padding, Math.ceil(rect.right) + this.padding);
  }

  private getMinTopPxForFab(): number {
    if (!isPlatformBrowser(this.platformId)) return this.padding;
    if (this.sideMenuService.menuLayout !== 'top') return this.padding;

    const bar = document.querySelector('.top-menu-wrapper');
    if (!bar) return this.padding + 65;

    const h = Math.ceil(bar.getBoundingClientRect().height);
    return Math.max(this.padding, h + this.padding);
  }

  /** Recarregar a página ou trocar lateral/top: posição volta ao CSS por defeito (sem persistir arrasto). */
  private resetFabPositionToDefault(): void {
    if (this.isInModalMode) return;
    this.position = null;
    this.clearStoredFabPosition();
    this.cdr.detectChanges();
    this.scheduleBubbleAlignRefresh();
  }

  private clearStoredFabPosition(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    try {
      localStorage.removeItem(this.storageKey);
    } catch {
      // Ignora quota / modo privado
    }
  }

  /** Ao mudar entre menu lateral e menu superior, repõe o FAB no canto por defeito. */
  private watchMenuLayoutReset(): void {
    this.sideMenuService.menuLayout$
      .pipe(distinctUntilChanged(), skip(1), takeUntil(this.destroy$))
      .subscribe(() => {
        this.resetFabPositionToDefault();
      });
  }

  /** Abrir/fechar/pin/mobile do menu lateral: re-clampa só se o utilizador tiver arrastado nesta sessão. */
  private watchSideMenuGeometry(): void {
    merge(
      this.sideMenuService.isOpen$,
      this.sideMenuService.isPinned$,
      this.sideMenuService.isMobile$
    )
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(() => {
        if (!isPlatformBrowser(this.platformId) || !this.position) return;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!this.position) return;
            this.clampFabPositionToSafeArea();
            this.cdr.markForCheck();
            this.scheduleBubbleAlignRefresh();
          });
        });
      });
  }

  private watchMenuLayoutForClassRefresh(): void {
    this.sideMenuService.menuLayout$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.cdr.markForCheck());
  }

  private watchFeedbackTrigger(): void {
    this.feedbackTriggerService.openPanel.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.clearFabHideTimer();
      this.isHidden = false;
      try {
        localStorage.removeItem(this.hideUntilKey);
      } catch {
        // Ignora quota / modo privado
      }
      this.isPanelOpen = true;
      this.captureAborted = false;
      this.contextMenuVisible = false;
      this.cdr.detectChanges();
    });
  }

  private watchAiButtonVisibility(): void {
    this.aiChatOverlayService.state$
      .pipe(
        map(state => state.visible),
        distinctUntilChanged(),
        filter(visible => visible),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.isInModalMode) return;
        this.resetFabPositionToDefault();
      });
  }

  /**
   * Elementos omitidos do raster do html2canvas.
   * Não ignorar máscaras PrimeNG (`.p-dialog-mask`, etc.): modais ficam no `body` e devem aparecer no print.
   * Para excluir outro widget no futuro, marque o container com `data-feedback-capture-exclude`.
   */
  private isExcludedFromFeedbackCapture(el: Element): boolean {
    if (el.tagName.toUpperCase() === 'APP-FLOATING-FEEDBACK-BUTTON') {
      return true;
    }
    if (el.closest('[data-feedback-capture-exclude]')) {
      return true;
    }
    const classList = (el as HTMLElement | SVGElement).classList;
    if (classList?.contains('ignore-export')) {
      return true;
    }
    return false;
  }

  private async captureScreenshot(options?: { skipPanelDelay?: boolean }): Promise<void> {
    // 350ms: primeira abertura do painel (transição ~300ms). Recaptura: sem espera extra.
    const delayMs = options?.skipPanelDelay === true ? 0 : 350;
    await new Promise<void>(resolve => setTimeout(resolve, delayMs));
    if (this.captureAborted) return;

    try {
      const html2canvas = (await import('html2canvas')).default;
      if (this.captureAborted) return;

      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        ignoreElements: (el: Element) => this.isExcludedFromFeedbackCapture(el),
        scale: 1,
        logging: false,
        imageTimeout: 5000,
      });
      if (this.captureAborted) return;

      this.screenshotBlob = await new Promise<Blob | null>(resolve =>
        canvas.toBlob(resolve, 'image/png', 0.85)
      );
      if (this.captureAborted) return;
    } catch {
      if (!this.captureAborted) this.screenshotBlob = null;
    }

    if (!this.captureAborted) {
      this.isCapturingScreenshot = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Recolhido só quando a página está rolada E nada pede o botão inteiro: arrastado pela pessoa, painel
   * aberto, gravando, arrastando agora ou menu de contexto aberto. Avaliado a cada renderização, então abrir
   * o painel ou começar a gravar traz o botão de volta na hora, sem esperar outro scroll (card 3822).
   */
  protected get isTuckActive(): boolean {
    return (
      this.fabTucked &&
      !this.position &&
      !this.isPanelOpen &&
      !this.isRecording &&
      !this.isDragging &&
      !this.contextMenuVisible
    );
  }

  protected get buttonStyle(): Record<string, string> {
    if (this.position) {
      return {
        left: `${this.position.left}px`,
        top: `${this.position.top}px`,
        right: 'auto',
        bottom: 'auto',
      };
    }
    return {};
  }

  private scheduleBubbleAlignRefresh(): void {
    if (typeof window === 'undefined') return;
    requestAnimationFrame(() => {
      this.refreshBubbleHorizontalAlign();
      requestAnimationFrame(() => this.refreshBubbleHorizontalAlign());
    });
  }

  private refreshBubbleHorizontalAlign(): void {
    if (typeof window === 'undefined') return;
    const el = this.fabRef?.nativeElement;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const next = centerX >= window.innerWidth / 2;
    if (this.bubbleOpensLeft === next) return;
    this.bubbleOpensLeft = next;
    this.cdr.detectChanges();
  }

  private clearBubbleReshowTimer(): void {
    if (this.bubbleReshowTimeoutId !== null) {
      clearTimeout(this.bubbleReshowTimeoutId);
      this.bubbleReshowTimeoutId = null;
    }
  }

  /** Repõe o balão após fechar o menu de contexto, exceto se o utilizador o dispensou com o X (timer ativo). */
  private restoreBubbleAfterContextMenu(): void {
    if (this.bubbleReshowTimeoutId !== null || this.isPanelOpen) return;
    this.showBubble = true;
    this.scheduleBubbleAlignRefresh();
  }

  private getPoint(event: MouseEvent | TouchEvent): { x: number; y: number } | null {
    if (event instanceof MouseEvent) return { x: event.clientX, y: event.clientY };
    const touch = event.touches?.[0] || event.changedTouches?.[0];
    return touch ? { x: touch.clientX, y: touch.clientY } : null;
  }
}
